<template>
  <div v-if="providerId && routingSupported && canView" class="border-t border-gray-100" data-testid="settlement-accounts-panel">
    <div class="px-5 py-3 flex items-center justify-between gap-3 bg-gray-50/50">
      <div>
        <h3 class="text-sm font-semibold text-gray-700">{{ copy.title }}</h3>
        <p class="text-[11px] text-gray-500 mt-0.5">{{ copy.description }}</p>
      </div>
      <Button
        v-if="canManage"
        size="small"
        :icon="showForm ? 'pi pi-minus' : 'pi pi-plus'"
        :label="showForm ? 'Cancel' : `Add ${copy.noun}`"
        text
        severity="secondary"
        @click="toggleForm()"
        data-testid="btn-add-settlement-account"
      />
    </div>

    <!-- Add/Edit Form -->
    <transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="max-h-0 opacity-0"
      enter-to-class="max-h-[900px] opacity-100"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="max-h-[900px] opacity-100"
      leave-to-class="max-h-0 opacity-0"
    >
      <div v-if="showForm" class="overflow-hidden border-t border-gray-100">
        <form class="px-5 py-4 space-y-3" novalidate @submit.prevent="submitForm">
          <div>
            <label for="settlement-label" class="block text-xs font-medium text-gray-600 mb-1">
              Name <small class="p-error">*</small>
            </label>
            <InputText
              id="settlement-label"
              v-model="form.label"
              placeholder="e.g. Tuition account"
              size="small"
              class="w-full"
              :invalid="!!fieldError('label')"
              data-testid="input-settlement-label"
            />
            <small v-if="fieldError('label')" class="p-error block mt-0.5" data-testid="error-settlement-label">{{ fieldError('label') }}</small>
            <p v-else class="text-[11px] text-gray-500 mt-0.5">A name your staff will recognise when choosing where a fee pays into.</p>
          </div>

          <!-- Provider-specific config fields -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div v-for="field in configFields" :key="field.key">
              <label :for="`config-${field.key}`" class="block text-xs font-medium text-gray-600 mb-1">
                {{ field.label }}
                <small v-if="fieldRequired(field)" class="p-error">*</small>
                <span v-else class="text-gray-500">(optional)</span>
              </label>
              <InputText
                :id="`config-${field.key}`"
                v-model="configForm[field.key]"
                :placeholder="configFieldPlaceholder(field, !!editingAccount)"
                :type="field.secret ? 'password' : 'text'"
                autocomplete="off"
                size="small"
                class="w-full"
                :invalid="!!fieldError(field.key)"
                :data-testid="`input-config-${field.key}`"
              />
              <small v-if="fieldError(field.key)" class="p-error block mt-0.5" :data-testid="`error-config-${field.key}`">{{ fieldError(field.key) }}</small>
              <template v-else>
                <Message
                  v-if="secretEditWarning(field, !!editingAccount, copy.noun)"
                  severity="warn"
                  size="small"
                  variant="simple"
                  class="mt-0.5"
                  :data-testid="`warning-config-${field.key}`"
                >
                  {{ secretEditWarning(field, !!editingAccount, copy.noun) }}
                </Message>
                <p class="text-[11px] text-gray-500 mt-0.5">{{ field.help }}</p>
              </template>
            </div>
          </div>

          <Message v-if="isInterswitch" severity="info" size="small" variant="simple" data-testid="settlement-environment-note">
            Test or live payments are decided by the server your school runs on, so there is nothing to choose here.
          </Message>

          <div>
            <button
              type="button"
              class="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
              :aria-expanded="showBankDetails"
              data-testid="btn-toggle-bank-details"
              @click="showBankDetails = !showBankDetails"
            >
              <i class="pi text-[10px]" :class="showBankDetails ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
              Bank details (optional, for your reference only)
            </button>
            <div v-if="showBankDetails" class="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
              <div>
                <label for="settlement-bank-name" class="block text-xs font-medium text-gray-600 mb-1">Bank name</label>
                <InputText id="settlement-bank-name" v-model="form.bank_name" placeholder="e.g. Zenith Bank" size="small" class="w-full" data-testid="input-settlement-bank-name" />
              </div>
              <div>
                <label for="settlement-account-number" class="block text-xs font-medium text-gray-600 mb-1">Account number</label>
                <InputText
                  id="settlement-account-number"
                  v-model="form.account_number"
                  placeholder="10 digits, e.g. 0123456789"
                  inputmode="numeric"
                  size="small"
                  class="w-full"
                  :invalid="!!fieldError('account_number')"
                  data-testid="input-settlement-account-number"
                />
                <small v-if="fieldError('account_number')" class="p-error block mt-0.5" data-testid="error-settlement-account-number">{{ fieldError('account_number') }}</small>
              </div>
              <div>
                <label for="settlement-account-name" class="block text-xs font-medium text-gray-600 mb-1">Account name</label>
                <InputText
                  id="settlement-account-name"
                  v-model="form.account_name"
                  placeholder="e.g. University Revenue Account"
                  size="small"
                  class="w-full"
                  data-testid="input-settlement-account-name" />
              </div>
            </div>
          </div>

          <div class="flex justify-end">
            <Button
              size="small"
              type="submit"
              :label="editingAccount ? 'Save changes' : `Save ${copy.noun}`"
              icon="pi pi-check"
              :loading="isCreating || isUpdating"
              data-testid="btn-save-settlement-account"
            />
          </div>
        </form>
      </div>
    </transition>

    <!-- Account List -->
    <div v-if="loading && !accounts.length" class="flex items-center justify-center py-6">
      <i class="pi pi-spin pi-spinner text-lg text-primary"></i>
    </div>

    <div v-else-if="error && !accounts.length" class="px-5 py-4" data-testid="settlement-accounts-error">
      <Message severity="error" size="small">
        <div class="flex items-center justify-between gap-3 flex-wrap">
          <span>We couldn't load the {{ copy.title }}.</span>
          <Button label="Try again" size="small" text icon="pi pi-refresh" data-testid="btn-retry-settlement-accounts" @click="settle(refetch())" />
        </div>
      </Message>
    </div>

    <div v-else-if="!accounts.length && !showForm" class="text-center py-6 px-5 text-gray-500" data-testid="settlement-accounts-empty">
      <p class="text-xs">No {{ copy.title }} yet. Students can't pay with this provider until you add one.</p>
      <Button
        v-if="canManage"
        :label="`Add ${copy.noun}`"
        icon="pi pi-plus"
        size="small"
        text
        class="mt-1"
        data-testid="btn-add-first-settlement-account"
        @click="toggleForm()"
      />
    </div>

    <div v-else-if="accounts.length" class="px-5 pb-4 pt-1 space-y-1.5">
      <div
        v-for="account in accounts"
        :key="account.id"
        class="flex items-center justify-between py-2 px-3 rounded-lg transition-colors"
        :class="account.is_default ? 'bg-primary/5' : 'hover:bg-gray-50'"
        :data-testid="`settlement-account-${account.id}`"
      >
        <div class="flex items-center gap-2.5 min-w-0">
          <div class="min-w-0">
            <div class="flex items-center gap-1.5">
              <p class="text-sm font-medium text-gray-900">{{ account.label }}</p>
              <Tag v-if="account.is_default" value="Default" severity="info" class="text-[10px]" />
              <Tag v-if="!account.is_active" value="Switched off" severity="secondary" class="text-[10px]" />
            </div>
            <p class="text-xs text-gray-500">
              <span v-if="summaryFor(account)" class="text-gray-600">{{ summaryFor(account) }}</span>
              <span v-if="account.bank_name" class="ml-1.5">
                · {{ account.bank_name }}
                <span v-if="account.account_number"> ****{{ account.account_number.slice(-4) }}</span>
              </span>
            </p>
          </div>
        </div>

        <div v-if="canManage" class="flex items-center gap-1 shrink-0">
          <button
            v-if="!account.is_default && account.is_active"
            v-tooltip.top="'Make this the default'"
            type="button"
            :aria-label="`Make ${account.label} the default ${copy.noun}`"
            class="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-primary transition-colors"
            :disabled="isSettingDefault"
            @click="setDefault(account.id, account.label)"
            :data-testid="`btn-set-default-${account.id}`"
          >
            <i class="pi pi-star text-xs"></i>
          </button>
          <button
            v-tooltip.top="'Edit'"
            type="button"
            :aria-label="`Edit ${account.label}`"
            class="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-primary transition-colors"
            @click="startEdit(account)"
            :data-testid="`btn-edit-settlement-${account.id}`"
          >
            <i class="pi pi-pencil text-xs"></i>
          </button>
          <span
            v-tooltip.top="account.is_default ? `The default ${copy.noun} can't be removed. Make another one the default first.` : 'Remove'"
            class="inline-flex"
          >
            <button
              type="button"
              :aria-label="account.is_default ? `${account.label} is the default and can't be removed` : `Remove ${account.label}`"
              class="p-1 rounded transition-colors"
              :class="account.is_default ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100 text-gray-500 hover:text-primary'"
              :disabled="account.is_default"
              @click="confirmDelete(account)"
              :data-testid="`btn-delete-settlement-${account.id}`"
            >
              <i class="pi pi-trash text-xs"></i>
            </button>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useConfirm } from 'primevue/useconfirm';
import { useVuelidate, type ValidationArgs } from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { can, AppPermissions } from '@/utils/permissions';
import { useUserStore } from '@/store/userStore';
import { useFormErrors } from '@/composables/useFormErrors';
import { accountNumberProblem } from '@/utils/bankAccount';
import { settle } from '@/utils/settle';
import { isCode } from '@/utils/billingLabels';
import { PaymentProviderType } from '@/types/composition-functions';
import {
  useGatewaySettlementAccounts,
  type GatewaySettlementAccount,
} from '@/composables/billing/useGatewaySettlementAccounts';
import {
  accountSummary,
  bankFieldsPayload,
  buildSettlementConfig,
  configFieldsFor,
  deleteAccountConfirmMessage,
  identifierKeyFor,
  isConfigFieldRequired,
  isDuplicateIdentifierError,
  configFieldPlaceholder,
  secretEditWarning,
  settlementSectionCopy,
  type SettlementConfigField,
} from '@/composables/billing/gatewaySettlementAccountForm';

const props = defineProps<{
  providerId: string | null;
  providerName?: string;
}>();

const confirm = useConfirm();
const userStore = useUserStore();
const providerIdRef = computed(() => props.providerId);

// Only Interswitch honours per-account routing end-to-end today (the other
// repositories ignore gateway_settlement_account_id at initiate time), so
// showing this panel for them would configure routing that never happens.
// Widen this list as per-provider override support lands in the backend.
const isInterswitch = computed(() => isCode(props.providerName, PaymentProviderType.Interswitch));
const routingSupported = isInterswitch;

const canManage = computed(() =>
  can(userStore.currentUser, AppPermissions.Billing.Actions.ManageGatewaySettlementAccounts, AppPermissions.Billing.Resource),
);
const canView = computed(() =>
  canManage.value ||
  can(userStore.currentUser, AppPermissions.Billing.Actions.ViewGatewaySettlementAccounts, AppPermissions.Billing.Resource),
);

const copy = computed(() => settlementSectionCopy(props.providerName));

const {
  accounts,
  loading,
  error,
  refetch,
  createAccount,
  isCreating,
  updateAccount,
  isUpdating,
  deleteAccount,
  setDefault,
  isSettingDefault,
} = useGatewaySettlementAccounts(providerIdRef, { noun: () => copy.value.noun });

const showForm = ref(false);
const showBankDetails = ref(false);
const editingAccount = ref<GatewaySettlementAccount | null>(null);

const form = reactive({
  label: '',
  bank_name: '',
  account_number: '',
  account_name: '',
});

const configForm = reactive<Record<string, string>>({});
const configFields = computed<SettlementConfigField[]>(() => configFieldsFor(props.providerName));
const identifierKey = computed(() => identifierKeyFor(props.providerName));

const fieldRequired = (field: SettlementConfigField) => isConfigFieldRequired(field, !!editingAccount.value);
const summaryFor = (account: GatewaySettlementAccount) => accountSummary(account, props.providerName);

const rules = computed(() => {
  const config: Record<string, ValidationArgs> = {};
  for (const field of configFields.value) {
    config[field.key] = fieldRequired(field)
      ? { required: helpers.withMessage(`Enter the ${field.label}.`, required) }
      : {};
  }
  return {
    form: {
      label: { required: helpers.withMessage('Give this a name, e.g. "Tuition account".', required) },
      account_number: {
        nuban: helpers.withMessage(
          () => accountNumberProblem(form.account_number),
          (value: string) => !accountNumberProblem(value),
        ),
      },
    },
    config,
  };
});

const v$ = useVuelidate(rules, { form, config: configForm });
const { handleServerErrors, clearErrors, getErrorMessage } = useFormErrors();
const duplicateIdentifierMessage = ref('');

type FieldState = { $errors?: Array<{ $message: unknown }> } | undefined;
function firstMessage(state: FieldState): string {
  const message = state?.$errors?.[0]?.$message;
  return typeof message === 'string' ? message : '';
}

/** Inline message for a field: client checks first, then what the server said. */
function fieldError(key: string): string {
  if (key === 'label' || key === 'account_number') {
    return firstMessage((v$.value.form as Record<string, FieldState>)?.[key]) || getErrorMessage(key);
  }
  const clientMessage = firstMessage((v$.value.config as Record<string, FieldState> | undefined)?.[key]);
  if (clientMessage) return clientMessage;
  if (key === identifierKey.value) {
    return duplicateIdentifierMessage.value || getErrorMessage('gateway_identifier');
  }
  return getErrorMessage(key);
}

function resetForm() {
  form.label = '';
  form.bank_name = '';
  form.account_number = '';
  form.account_name = '';
  editingAccount.value = null;
  showBankDetails.value = false;
  for (const key of Object.keys(configForm)) delete configForm[key];
  for (const field of configFields.value) {
    configForm[field.key] = '';
  }
  clearErrors();
  duplicateIdentifierMessage.value = '';
  v$.value.$reset();
}

function toggleForm() {
  if (showForm.value) {
    showForm.value = false;
    resetForm();
  } else {
    resetForm();
    showForm.value = true;
  }
}

function startEdit(account: GatewaySettlementAccount) {
  resetForm();
  editingAccount.value = account;
  form.label = account.label;
  form.bank_name = account.bank_name ?? '';
  form.account_number = account.account_number ?? '';
  form.account_name = account.account_name ?? '';
  showBankDetails.value = !!(form.bank_name || form.account_number || form.account_name);

  const config = account.config as Record<string, string> | null;
  for (const field of configFields.value) {
    configForm[field.key] = field.secret ? '' : (config?.[field.key] ?? '');
  }
  showForm.value = true;
}

async function submitForm() {
  clearErrors();
  duplicateIdentifierMessage.value = '';
  if (!(await v$.value.$validate())) {
    if (v$.value.form.account_number.$error) showBankDetails.value = true;
    return;
  }

  const editing = editingAccount.value;
  const config = buildSettlementConfig(configFields.value, configForm, editing?.config ?? null);
  const identifier = (configForm[identifierKey.value] ?? '').trim();
  const bank = bankFieldsPayload(form, !!editing);

  try {
    if (editing) {
      await updateAccount({
        id: editing.id,
        label: form.label.trim(),
        gateway_identifier: identifier,
        config,
        ...bank,
      });
    } else {
      await createAccount({
        payment_provider_id: props.providerId!,
        label: form.label.trim(),
        gateway_identifier: identifier,
        config,
        bank_name: bank.bank_name ?? undefined,
        account_number: bank.account_number ?? undefined,
        account_name: bank.account_name ?? undefined,
      });
    }
    showForm.value = false;
    resetForm();
  } catch (err) {
    // The composable already showed a toast; put field problems next to the fields.
    handleServerErrors(err);
    if (isDuplicateIdentifierError(err)) {
      const label = configFields.value.find((field) => field.key === identifierKey.value)?.label ?? 'ID';
      duplicateIdentifierMessage.value = `This ${label} is already set up. Use a different one, or edit the existing ${copy.value.noun}.`;
    }
    if (getErrorMessage('account_number')) showBankDetails.value = true;
  }
}

function confirmDelete(account: GatewaySettlementAccount) {
  if (account.is_default) return;
  confirm.require({
    message: deleteAccountConfirmMessage(account.label, copy.value.noun),
    header: `Remove ${copy.value.noun}?`,
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Remove',
    rejectProps: { label: 'Keep it', severity: 'secondary', outlined: true },
    acceptClass: 'p-button-danger',
    accept: async() => {
      await deleteAccount(account.id, account.label);
    },
  });
}
</script>
